import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { projectService } from '../../services/projectService';
import ActionMenu from '../../components/common/ActionMenu';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import Input from '../../components/common/Input';
import Pagination from '../../components/common/Pagination';
import SearchableSelect from '../../components/common/SearchableSelect';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';

const LIMIT = 20;

const STATUS_OPTIONS = [
   { id: '', name: 'All Statuses' },
   { id: 'ACTIVE', name: 'Active' },
   { id: 'COMPLETED', name: 'Completed' },
   { id: 'ON_HOLD', name: 'On Hold' },
   { id: 'CANCELLED', name: 'Cancelled' },
];

const statusBadge = (status) => {
   const map = {
      ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      COMPLETED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      ON_HOLD: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
   };
   const label = {
      ACTIVE: 'Active',
      COMPLETED: 'Completed',
      ON_HOLD: 'On Hold',
      CANCELLED: 'Cancelled',
   };
   return (
      <span
         className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || ''}`}
      >
         {label[status] || status}
      </span>
   );
};

const Projects = () => {
   const navigate = useNavigate();
   const [projects, setProjects] = useState([]);
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [total, setTotal] = useState(0);
   const [search, setSearch] = useState('');
   const [statusFilter, setStatusFilter] = useState('');
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);

   const fetchProjects = useCallback(async () => {
      try {
         setLoading(true);
         const res = await projectService.getAll({
            page,
            limit: LIMIT,
            status: statusFilter || undefined,
            search: search.trim() || undefined,
         });
         setProjects(res.data);
         setTotalPages(res.pagination.totalPages);
         setTotal(res.pagination.total);
      } catch {
         toast.error('Failed to fetch projects');
      } finally {
         setLoading(false);
      }
   }, [page, statusFilter, search]);

   useEffect(() => {
      fetchProjects();
   }, [fetchProjects]);

   const handleSearchChange = (e) => {
      setSearch(e.target.value);
      setPage(1);
   };

   const handleStatusChange = (value) => {
      setStatusFilter(value);
      setPage(1);
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await projectService.delete(deleteTarget.id);
         toast.success('Project deleted successfully');
         setDeleteTarget(null);
         fetchProjects();
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to delete project');
      } finally {
         setDeleting(false);
      }
   };

   return (
      <PanelLayout>
         <div className="space-y-6">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Projects</h1>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                     Manage client and work projects
                  </p>
               </div>
               <Button onClick={() => navigate('/admin/projects/new')}>New Project</Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
               <div className="flex-1 max-w-sm">
                  <Input
                     value={search}
                     onChange={handleSearchChange}
                     placeholder="Search by title or client…"
                  />
               </div>
               <div className="w-48">
                  <SearchableSelect
                     label=""
                     searchable={false}
                     options={STATUS_OPTIONS}
                     placeholder="Select Status"
                     value={statusFilter}
                     onChange={handleStatusChange}
                     className="!mb-0"
                  />
               </div>
            </div>

            {loading ? (
               <Loader />
            ) : (
               <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Title
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Client
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Tech Stack
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Status
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Created
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                        {projects.length === 0 ? (
                           <tr>
                              <td
                                 colSpan="6"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 No projects found
                              </td>
                           </tr>
                        ) : (
                           projects.map((project) => (
                              <tr
                                 key={project.id}
                                 onClick={() => navigate(`/admin/projects/${project.id}`)}
                                 className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                              >
                                 <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                       {project.title}
                                    </div>
                                    {project.description && (
                                       <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xs truncate">
                                          {project.description}
                                       </div>
                                    )}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                       {project.clientName || 'N/A'}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                       {Array.isArray(project.techStack) &&
                                       project.techStack.length > 0 ? (
                                          project.techStack.slice(0, 4).map((tech) => (
                                             <span
                                                key={tech}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                             >
                                                {tech}
                                             </span>
                                          ))
                                       ) : (
                                          <span className="text-sm text-gray-400">N/A</span>
                                       )}
                                       {Array.isArray(project.techStack) &&
                                          project.techStack.length > 4 && (
                                             <span className="text-xs text-gray-400">
                                                +{project.techStack.length - 4}
                                             </span>
                                          )}
                                    </div>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    {statusBadge(project.status)}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                       <DateTimeDisplay dateStr={project.createdAt} />
                                    </span>
                                 </td>
                                 <td
                                    className="px-6 py-4 whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                 >
                                    <ActionMenu
                                       items={[
                                          {
                                             label: 'Edit',
                                             onClick: () =>
                                                navigate(`/admin/projects/${project.id}`),
                                          },
                                          {
                                             label: 'Delete',
                                             variant: 'danger',
                                             onClick: () => setDeleteTarget(project),
                                          },
                                       ]}
                                    />
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>
            )}

            <Pagination
               page={page}
               totalPages={totalPages}
               total={total}
               limit={LIMIT}
               onPageChange={setPage}
            />
         </div>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Project"
            message={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default Projects;
